import { NotificationEnum } from "@graphql/generated/graphql.g";
import { useClientsByUnresolvedNotificationsQuery } from "@graphql/clients/clients.g";
import { BarDatum } from "@nivo/bar";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import { BarChart } from "@design-system/BarChart/BarChart";

import { routes } from "@pages/routes";
import {
  ALL_NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABELS,
} from "@utils/notificationTypes";

const CHART_TYPE_LABELS: Partial<Record<NotificationEnum, string>> = {
  [NotificationEnum.SharedLogin]: "Shared login",
  [NotificationEnum.SaasDiscovered]: "Unreviewed app",
  [NotificationEnum.NonWhitelistedEmail]: "Non-whitelisted email",
  [NotificationEnum.InactivityRecorded]: "Inactivity recorded",
  [NotificationEnum.AccessToUnapprovedApp]: "Unapproved app access",
  [NotificationEnum.Signup]: "Signup",
};

export const TopUnresolvedNotificationsChart = () => {
  const navigate = useNavigate();
  const clientsByUnresolvedNotificationsQuery =
    useClientsByUnresolvedNotificationsQuery();

  const handleDetailsClick = useCallback(
    (clientId: string) => {
      navigate(
        routes.portal.clients.client.notifications.$buildPath({
          params: { clientId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const clientsWithNotifications: BarDatum[] = useMemo(() => {
    const received_data =
      clientsByUnresolvedNotificationsQuery.data?.clients?.edges
        .map((client) => {
          const counts = Object.fromEntries(
            ALL_NOTIFICATION_TYPES.map((type) => {
              const count =
                client?.node?.notifications?.aggregate?.groupBy?.notificationGroups?.find(
                  (notificationGroup) =>
                    notificationGroup?.notificationType === type,
                )?.count || 0;
              return [type, count.toString()];
            }),
          ) as Record<NotificationEnum, string>;

          return {
            id: client?.node?.id || "",
            client: client?.node?.name || "Unknown Tenant",
            ...counts,
          };
        })
        .filter((client) =>
          ALL_NOTIFICATION_TYPES.some((type) => Number(client[type]) > 0),
        );
    if (!received_data) return [];
    return received_data;
  }, [clientsByUnresolvedNotificationsQuery.data]);

  return (
    <BarChart
      chartData={clientsWithNotifications}
      onDetailsClick={(clientRecord: BarDatum) =>
        handleDetailsClick(clientRecord.id.toString())
      }
      keyMap={Object.fromEntries(
        ALL_NOTIFICATION_TYPES.map((type) => [
          type,
          CHART_TYPE_LABELS[type] ?? NOTIFICATION_TYPE_LABELS[type],
        ]),
      )}
    />
  );
};
